import { router, useLocalSearchParams } from 'expo-router';
import { produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useCartStore, useCartValues } from '@fhs/cart/src/state/cart-store';
import type { Schema } from '@fhs/client';
import { isWeb } from '@fhs/utils';
import { create, devtools } from '@fhs/zustand';
import { IIncentiveSelection } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { parseEntry } from '@fhs-legacy/frontend/src/state/global-state/models/loyalty/offers/offers.utils';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';
import { ICartEntry } from '@rbi-ctg/menu';

import { useAddIncentiveToCart } from '../hooks/use-add-incentive-to-cart';
import { useOfferDetailUi } from '../queries/loyalty.queries';

type OfferGuideCartState = {
  onBackAttempt?: () => boolean;
  offerInfo?: Omit<SimplyOfferType, 'steps'>;
  steps: Step[];
  currentStepIndex: number;
  isGoingForward: boolean;
  isEdit: boolean;
  showSummary: boolean;
  selectedEligibleItem?: Schema['MenuSectionItem']['type'];
  selectedTierId?: string;
  enableSimplifiedMenuDetailView: boolean;
  cartSuggestionsResolved: boolean;
};

type GoToStepParams = {
  index: number;
  immediate?: boolean;
  isEdit?: boolean;
};

type OfferGuideCartActions = {
  modifyItemSelectionInStep: (
    step: number,
    cartEntry: ICartEntry,
    shouldGoToNextStep?: boolean
  ) => void;
  resetItemSelectionInStep: (step: number) => void;
  goToStep: ({ index, immediate, isEdit }: GoToStepParams) => void;
  goToPrevStep: () => void;
  goToNextStep: () => void;
  goToNextNotDoneStep: () => void;
  goFromScratch: () => void;
  goBackToCartSuggestions: () => void;
  applyPreSelectedItems: (cartEntries: ICartEntry[]) => void;
  applySelectedItem: (cartEntry: ICartEntry) => void;
  setOffer: (offer: SimplyOfferType, enableSimplifiedMenuDetailView?: boolean) => void;
  setSelectedEligibleItem: (
    eligibleItem?: Schema['MenuSectionItem']['type'],
    isEdit?: boolean
  ) => void;
  setSelectedTierId: (tierId?: string) => void;
  reset: () => void;
  setOnBackAttempt: (callback: (() => boolean) | undefined) => void;
};

const initialState: OfferGuideCartState = {
  onBackAttempt: undefined,
  offerInfo: undefined,
  steps: [],
  currentStepIndex: 0,
  isGoingForward: true,
  isEdit: false,
  showSummary: false,
  selectedEligibleItem: undefined,
  selectedTierId: undefined,
  enableSimplifiedMenuDetailView: false,
  cartSuggestionsResolved: false,
};

export type Step = Schema['Step']['type'] & {
  selectedEntry?: ICartEntry;
  selectedEntryImage?: string;
};

export type SimplyOfferType = Schema['OfferDetail']['type'] & {
  steps: Step[];
};

const _isValidIndex = (newIndex: number, stepsLength: number) => {
  if (newIndex < 0 || newIndex >= stepsLength) {
    return false;
  }

  return true;
};

const _getNextNotDoneStep = (steps: Step[]) => {
  const lastIndexWithoutProduct = steps.findIndex(step => !step.selectedEntry);

  if (lastIndexWithoutProduct === -1) {
    return steps.length;
  }
  return lastIndexWithoutProduct;
};

const addCartEntryUrl = (
  cartEntry: ICartEntry,
  enableSimplifiedMenuDetailView: boolean,
  eligibleItem?: Schema['MenuSectionItem']['type']
) => {
  return {
    ...cartEntry,
    url:
      (enableSimplifiedMenuDetailView ? eligibleItem?.url : eligibleItem?._legacyUrl) ||
      cartEntry.url,
  } as ICartEntry;
};

export const useOfferCart = create<OfferGuideCartState & OfferGuideCartActions>()(
  devtools((set, get) => {
    const goToStep = ({ index, immediate = false, isEdit = false }: GoToStepParams) => {
      const customSetTimeout = immediate || isWeb ? r => r() : setTimeout;
      const state = get();
      const stepsLength = state.steps.length;
      if (index === stepsLength) {
        customSetTimeout(() => {
          set({
            currentStepIndex: index,
            showSummary: true,
            isEdit: false,
            selectedEligibleItem: undefined,
          });
        }, 1);
        set({ isGoingForward: true });
        return;
      }

      const step = state.steps[index];
      const selectedEligibleItem = isEdit
        ? step?.eligibleItems?.find(eligibleItem => eligibleItem.id === step.selectedEntry?._id)
        : undefined;

      if (_isValidIndex(index, stepsLength)) {
        customSetTimeout(() => {
          set({
            currentStepIndex: index,
            showSummary: false,
            isEdit: isEdit && !!selectedEligibleItem,
            selectedEligibleItem,
          });
        }, 1);
        if (index < state.currentStepIndex) {
          set({ isGoingForward: false });
          return;
        }
        set({ isGoingForward: true });
      }
    };
    return {
      ...initialState,
      reset: () => set(initialState),
      setSelectedEligibleItem: (eligibleItem?: Schema['MenuSectionItem']['type'], isEdit = false) =>
        set({ selectedEligibleItem: eligibleItem, isEdit }),
      setOnBackAttempt: (callback: (() => boolean) | undefined) => set({ onBackAttempt: callback }),
      goToNextStep: () => goToStep({ index: get().currentStepIndex + 1 }),
      goToNextNotDoneStep: () => goToStep({ index: _getNextNotDoneStep(get().steps) }),
      goToPrevStep: () => goToStep({ index: get().currentStepIndex - 1 }),
      goToStep,
      setSelectedTierId: (tierId?: string) =>
        set(state => ({
          selectedTierId: tierId,
          steps: state?.offerInfo?.tiers?.find(tier => tier.id === tierId)?.steps ?? [],
          selectedEligibleItem: undefined,
        })),
      applyPreSelectedItems: (cartEntries: ICartEntry[]) =>
        set(state => {
          const newSteps = produce(state.steps, draftSteps => {
            draftSteps.forEach(draftStep => {
              const selectedEntry = cartEntries.find(
                cartEntry => cartEntry.cartId === draftStep.preSelectedItem
              );

              if (selectedEntry) {
                const eligibleItem = draftStep.eligibleItems.find(
                  item => item.id === selectedEntry?._id
                );

                draftStep.selectedEntry = addCartEntryUrl(
                  selectedEntry,
                  state.enableSimplifiedMenuDetailView,
                  eligibleItem
                );
                draftStep.selectedEntryImage = eligibleItem?.image?.asset?.uri;
              }
            });
          });

          const nextStepIndex = _getNextNotDoneStep(newSteps);
          goToStep({ index: nextStepIndex, immediate: true });

          return { steps: newSteps, cartSuggestionsResolved: true };
        }),
      applySelectedItem: (cartEntry: ICartEntry) =>
        set(state => {
          const newSteps = produce(state.steps, draftSteps => {
            draftSteps.forEach(draftStep => {
              const eligibleItem = draftStep.eligibleItems.find(item => item.id === cartEntry?._id);

              draftStep.selectedEntry = addCartEntryUrl(
                cartEntry,
                state.enableSimplifiedMenuDetailView,
                eligibleItem
              );
              draftStep.selectedEntryImage = eligibleItem?.image?.asset?.uri;
            });
          });

          return { steps: newSteps, cartSuggestionsResolved: true };
        }),
      goFromScratch: () => set({ cartSuggestionsResolved: true }),
      goBackToCartSuggestions: () => set({ cartSuggestionsResolved: false }),
      setOffer: (offer: SimplyOfferType, enableSimplifiedMenuDetailView = false) => {
        const { steps, ...offerInfo } = offer;
        set({ steps, offerInfo, enableSimplifiedMenuDetailView });
      },
      modifyItemSelectionInStep: (step: number, cartEntry: ICartEntry, shouldGoToNextStep = true) =>
        set(state => {
          const newSteps = produce(state.steps, draftSteps => {
            const draftStep = draftSteps[step];
            const eligibleItem = draftStep.eligibleItems?.find(item => item.id === cartEntry._id);

            draftStep.selectedEntry = addCartEntryUrl(
              cartEntry,
              state.enableSimplifiedMenuDetailView,
              eligibleItem
            );
            draftStep.selectedEntryImage = eligibleItem?.image?.asset?.uri;
          });

          if (shouldGoToNextStep) {
            const nextStepIndex = _getNextNotDoneStep(newSteps);
            goToStep({ index: nextStepIndex });
          }

          return { steps: newSteps };
        }),
      resetItemSelectionInStep: (step: number) =>
        set(state => {
          const newSteps = produce(state.steps, draftSteps => {
            const draftStep = draftSteps[step];
            draftStep.selectedEntry = undefined;
            draftStep.selectedEntryImage = undefined;
          });
          return { steps: newSteps };
        }),
    };
  })
);

export const useIsOfferCartDirty = () => {
  const steps = useOfferCart(state => state.steps);
  const isDirty = useMemo(() => steps.some(step => !!step.selectedEntry), [steps]);

  return isDirty;
};

export const useIsItemInCart = () => {
  const steps = useOfferCart(state => state.steps);
  const cartSuggestionsResolved = useOfferCart(state => state.cartSuggestionsResolved);
  const isItemInCart = useMemo(
    () => !cartSuggestionsResolved && steps.some(step => !!step.preSelectedItem),
    [steps, cartSuggestionsResolved]
  );

  return isItemInCart;
};

export const useIsAvailableCartItemsInCart = () => {
  const steps = useOfferCart(state => state.steps);
  const cartSuggestionsResolved = useOfferCart(state => state.cartSuggestionsResolved);
  const isAvailableCartItemsInCart = useMemo(
    () =>
      !cartSuggestionsResolved &&
      steps.some(step => step.availableCartItems && step.availableCartItems?.length > 0),
    [steps, cartSuggestionsResolved]
  );

  return isAvailableCartItemsInCart;
};

export const useHasCartSuggestions = () => {
  const steps = useOfferCart(state => state.steps);
  const cartSuggestionsResolved = useOfferCart(state => state.cartSuggestionsResolved);

  const hasPreSelectedItem = useMemo(() => steps.some(step => !!step.preSelectedItem), [steps]);
  const hasAvailableCartItems = useMemo(
    () => steps.some(step => step.availableCartItems && step.availableCartItems?.length > 0),
    [steps]
  );

  return {
    hasCartSuggestions: hasPreSelectedItem || hasAvailableCartItems,
    isResolved: cartSuggestionsResolved,
  };
};

export const useSelectedStep = () => {
  const steps = useOfferCart(state => state.steps);
  const currentStepIndex = useOfferCart(state => state.currentStepIndex);
  const selectedStep = useMemo(() => steps[currentStepIndex], [steps, currentStepIndex]);

  return selectedStep;
};

export const useShouldRenderTierSelector = () => {
  const offerInfo = useOfferCart(state => state.offerInfo);
  const selectedTierId = useOfferCart(state => state.selectedTierId);
  return Boolean(offerInfo?.tiers && offerInfo.tiers?.length > 1 && !selectedTierId);
};

export const useEditStepSelectedEntry = () => {
  const goToStep = useOfferCart(state => state.goToStep);

  const editStepSelectedEntry = useCallback(
    (stepIndex: number) => {
      goToStep({ index: stepIndex, isEdit: true });
    },
    [goToStep]
  );

  return { editStepSelectedEntry };
};

const applyOfferCartChanges = (
  originalCartEntries: ICartEntry[],
  modifiedCartEntries: ICartEntry[],
  selectionsCartEntries: ICartEntry[]
) => {
  const entriesToRemove = new Set<string>();
  const quantityUpdates = new Map<string, { quantity: number; price: number }>();

  originalCartEntries.forEach(originalEntry => {
    const modifiedEntry = modifiedCartEntries.find(e => e.cartId === originalEntry.cartId);
    if (modifiedEntry && modifiedEntry.quantity !== originalEntry.quantity) {
      if (modifiedEntry.quantity <= 0) {
        entriesToRemove.add(originalEntry.cartId);
      } else {
        quantityUpdates.set(originalEntry.cartId, {
          quantity: modifiedEntry.quantity,
          price: modifiedEntry.price ?? originalEntry.price ?? 0,
        });
      }
    }
  });

  const { cartEntries: currentEntries, replaceItemsInCartStore } = useCartStore.getState();

  const updatedEntries = currentEntries
    .filter(entry => !entriesToRemove.has(entry.cartId))
    .map(entry => {
      const update = quantityUpdates.get(entry.cartId);
      if (update) {
        return { ...entry, quantity: update.quantity, price: update.price };
      }
      return entry;
    })
    .concat(selectionsCartEntries);

  replaceItemsInCartStore(updatedEntries);
};

export const useAddGuideOfferToCart = () => {
  const { cartEntries } = useCartValues();
  const { addIncentiveToCart } = useAddIncentiveToCart();

  const addGuideOfferToCart = useCallback(
    (dryRun = false) => {
      const {
        offerInfo,
        steps: offerSteps,
        reset,
        selectedTierId,
        resetItemSelectionInStep,
      } = useOfferCart.getState();
      if (offerInfo) {
        const selections: IIncentiveSelection[] = [];
        const cartEntriesClone = cloneDeep(cartEntries);
        const selectionsCartEntries: ICartEntry[] = [];
        offerSteps.forEach(step => {
          const selectedEntry = step.selectedEntry;
          if (!selectedEntry) {
            return;
          }

          const itemInCart = cartEntriesClone.find(
            cartEntry => cartEntry.cartId === step.selectedEntry?.cartId
          );

          if (itemInCart && itemInCart.price) {
            const itemClone = cloneDeep(selectedEntry);
            const individualPrice = itemInCart.price
              ? Math.round(itemInCart.price / itemInCart.quantity)
              : 0;
            itemClone.quantity = 1;
            itemClone.price = individualPrice;
            itemClone.cartId = uuidv4();
            itemClone.lineId = itemClone.cartId;
            selectionsCartEntries.push(itemClone);

            selections.push({
              incentiveId: offerInfo.id,
              key: step.key,
              lineId: itemClone?.cartId ?? '',
              quantity: 1,
              rank: step.rank,
              tierKey: selectedTierId,
            });

            itemInCart.quantity--;
            itemInCart.price = itemInCart.quantity * individualPrice;
            return;
          }

          selections.push({
            incentiveId: offerInfo.id,
            key: step.key,
            lineId: selectedEntry?.cartId,
            quantity: 1,
            rank: step.rank,
            tierKey: selectedTierId,
          });
          selectionsCartEntries.push(selectedEntry);
        });

        if (!dryRun) {
          const isSingleStepAndShouldShowItemPrice =
            offerSteps.length === 1 && offerSteps[0].shouldShowItemPrice;
          addIncentiveToCart({
            offer: offerInfo,
            selections,
            showMessage: true,
            callbacks: {
              onSuccess: async () => {
                reset();
                router.dismiss();
                applyOfferCartChanges(cartEntries, cartEntriesClone, selectionsCartEntries);
                setTimeout(() => {
                  if (isWeb) {
                    router.replace(routes.menu);
                  } else {
                    router.dismissTo(routes.menu);
                  }
                }, 1);
              },
              onDismiss: isSingleStepAndShouldShowItemPrice
                ? () => {
                  resetItemSelectionInStep(0);
                }
                : undefined,
            },
          });
        }

        return {
          selections,
          backendCartEntries: selectionsCartEntries?.reduce(parseEntry(), []),
        };
      }
      return null;
    },
    [cartEntries, addIncentiveToCart]
  );

  return { addGuideOfferToCart };
};

export const useHandleOfferItemSelection = () => {
  const steps = useOfferCart(state => state.steps);
  const { addGuideOfferToCart } = useAddGuideOfferToCart();
  const modifyItemSelectionInStep = useOfferCart(state => state.modifyItemSelectionInStep);
  const setSelectedEligibleItem = useOfferCart(state => state.setSelectedEligibleItem);
  return useCallback(
    (currentStepIndex: number, cartEntry: ICartEntry) => {
      const shouldAddToCart = steps[currentStepIndex]?.shouldShowItemPrice && steps.length === 1;

      modifyItemSelectionInStep(currentStepIndex, cartEntry, !shouldAddToCart);
      if (shouldAddToCart) {
        addGuideOfferToCart();
        setSelectedEligibleItem(undefined);
      }
    },
    [steps, addGuideOfferToCart, modifyItemSelectionInStep, setSelectedEligibleItem]
  );
};

export const useIsLoadingTierSelector = () => {
  const { offerId, tierSelectorRequired } = useLocalSearchParams<{
    offerId: string;
    tierSelectorRequired: string;
  }>();
  const { isLoading } = useOfferDetailUi(offerId);

  return tierSelectorRequired === 'true' && isLoading;
};
